import { PushNotifications } from '@capacitor/push-notifications'
import type { PermissionState } from '@capacitor/core'
import toast from 'react-hot-toast'
import { api } from '../lib/api'
import { isNativeAndroid, isNativeIOS } from '../mobile/capacitor'
import { handleNotificationRoute } from './notificationRouting'

const ANDROID_PUSH_TOKEN_KEY = 'optioneer_android_push_token'
const IOS_PUSH_TOKEN_KEY = 'optioneer_ios_push_token'
const PUSH_DEVICE_ID_KEY = 'optioneer_push_device_id'

type NativePushPlatform = 'android' | 'ios'

export type AppPushStatus =
  | 'unsupported'
  | 'enabled'
  | 'permission_required'
  | 'registration_required'
  | 'blocked'
  | 'registering'
  | 'error'

let listenersConfigured = false
let listenersConfiguring: Promise<void> | null = null
let registering = false
let pendingRegistrationPromise: Promise<AppPushStatus> | null = null
let pendingRegistrationResolver: ((status: AppPushStatus) => void) | null = null
let pendingRegistrationTimeout: ReturnType<typeof setTimeout> | null = null

function getOrCreateDeviceId() {
  let deviceId = window.localStorage.getItem(PUSH_DEVICE_ID_KEY)
  if (!deviceId) {
    deviceId =
      globalThis.crypto?.randomUUID?.() ??
      `native-${Date.now()}-${Math.random().toString(36).slice(2)}`
    window.localStorage.setItem(PUSH_DEVICE_ID_KEY, deviceId)
  }
  return deviceId
}

function getNativePushPlatform(): NativePushPlatform | null {
  if (isNativeAndroid()) return 'android'
  if (isNativeIOS()) return 'ios'
  return null
}

function tokenStorageKey(platform: NativePushPlatform) {
  return platform === 'android' ? ANDROID_PUSH_TOKEN_KEY : IOS_PUSH_TOKEN_KEY
}

function getStoredPushToken(platform: NativePushPlatform) {
  return window.localStorage.getItem(tokenStorageKey(platform))
}

function setStoredPushToken(platform: NativePushPlatform, token: string) {
  window.localStorage.setItem(tokenStorageKey(platform), token)
}

function clearPendingRegistration() {
  if (pendingRegistrationTimeout) {
    clearTimeout(pendingRegistrationTimeout)
    pendingRegistrationTimeout = null
  }
  pendingRegistrationPromise = null
  pendingRegistrationResolver = null
  registering = false
}

function completePendingRegistration(status: AppPushStatus) {
  const resolver = pendingRegistrationResolver
  clearPendingRegistration()
  resolver?.(status)
}

function waitForBackendRegistration() {
  if (pendingRegistrationPromise) return pendingRegistrationPromise

  pendingRegistrationPromise = new Promise<AppPushStatus>((resolve) => {
    pendingRegistrationResolver = resolve
    pendingRegistrationTimeout = setTimeout(() => {
      completePendingRegistration('registration_required')
    }, 15000)
  })
  return pendingRegistrationPromise
}

function permissionToStatus(permission: PermissionState): AppPushStatus {
  if (permission === 'granted') return 'enabled'
  if (permission === 'denied') return 'blocked'
  return 'permission_required'
}

async function createAndroidChannels() {
  if (!isNativeAndroid()) return

  await Promise.allSettled([
    PushNotifications.createChannel({
      id: 'optioneer_alerts_critical',
      name: 'Critical Alerts',
      description: 'Profit target, stop loss, and urgent account alerts',
      importance: 5,
      visibility: 1,
      vibration: true,
      lights: true,
      lightColor: '#ef4444',
    }),
    PushNotifications.createChannel({
      id: 'optioneer_alerts_expiry',
      name: 'Expiry Warnings',
      description: 'Option expiry and auto-exercise alerts',
      importance: 4,
      visibility: 1,
      vibration: true,
    }),
    PushNotifications.createChannel({
      id: 'optioneer_alerts_general',
      name: 'General Alerts',
      description: 'General Optioneer notifications',
      importance: 3,
      visibility: 1,
    }),
  ])
}

async function configureListeners() {
  const platform = getNativePushPlatform()
  if (listenersConfigured || !platform) return
  if (listenersConfiguring) return listenersConfiguring

  listenersConfiguring = (async () => {
    await PushNotifications.addListener('registration', (token) => {
      setStoredPushToken(platform, token.value)
      void api.mobile
        .registerPushToken({
          platform,
          token: token.value,
          device_id: getOrCreateDeviceId(),
          app_version: import.meta.env.VITE_APP_VERSION || null,
          os_version: navigator.userAgent,
          environment: import.meta.env.PROD ? 'production' : 'development',
          permission_status: 'granted',
        })
        .then(() => {
          completePendingRegistration('enabled')
        })
        .catch((error) => {
          console.error('[Push] Failed to register token with backend:', error)
          completePendingRegistration('error')
        })
    })

    await PushNotifications.addListener('registrationError', (error) => {
      console.error('[Push] Registration error:', error.error)
      completePendingRegistration('error')
    })

    await PushNotifications.addListener('pushNotificationReceived', (notification) => {
      window.dispatchEvent(
        new CustomEvent('optioneer:push-notification-received', { detail: notification })
      )

      if (notification.title || notification.body) {
        toast(
          `${notification.title || 'Optioneer alert'}${notification.body ? `: ${notification.body}` : ''}`
        )
      }
    })

    await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      void handleNotificationRoute(action.notification.data)
    })

    listenersConfigured = true
  })()

  try {
    await listenersConfiguring
  } finally {
    listenersConfiguring = null
  }
}

export async function initializeAppPushListeners() {
  try {
    await configureListeners()
  } catch (error) {
    console.warn('[Push] Failed to initialize push listeners:', error)
  }
}

export async function getAppPushStatus(): Promise<AppPushStatus> {
  const platform = getNativePushPlatform()
  if (!platform) return 'unsupported'

  try {
    const permission = await PushNotifications.checkPermissions()
    const status = permissionToStatus(permission.receive)
    if (status === 'enabled' && !getStoredPushToken(platform)) return 'registration_required'
    return status
  } catch {
    return 'error'
  }
}

export async function requestAndRegisterAppPush(): Promise<AppPushStatus> {
  if (!getNativePushPlatform()) return 'unsupported'
  if (registering) return pendingRegistrationPromise ?? 'registering'

  try {
    await configureListeners()
    await createAndroidChannels()

    const permission = await PushNotifications.requestPermissions()
    const status = permissionToStatus(permission.receive)
    if (status !== 'enabled') {
      return status
    }

    registering = true
    const registrationStatus = waitForBackendRegistration()
    await PushNotifications.register()
    return await registrationStatus
  } catch (error) {
    completePendingRegistration('error')
    console.error('[Push] Registration failed:', error)
    return 'error'
  }
}

export async function registerAppPushIfPermitted() {
  const platform = getNativePushPlatform()
  if (!platform) return
  if (!getStoredPushToken(platform)) return
  if (registering) {
    await pendingRegistrationPromise
    return
  }

  try {
    await configureListeners()
    const permission = await PushNotifications.checkPermissions()
    if (permission.receive !== 'granted') return
    await createAndroidChannels()
    registering = true
    const registrationStatus = waitForBackendRegistration()
    await PushNotifications.register()
    await registrationStatus
  } catch (error) {
    completePendingRegistration('error')
    console.warn('[Push] Background registration failed:', error)
  }
}

export async function revokeCurrentDevicePushToken() {
  const platform = getNativePushPlatform()
  if (!platform) return
  const token = getStoredPushToken(platform)
  const deviceId = window.localStorage.getItem(PUSH_DEVICE_ID_KEY)

  if (token || deviceId) {
    await api.mobile.revokeCurrentPushToken({ token, device_id: deviceId })
  }

  try {
    await PushNotifications.unregister()
  } catch {
    // Unregister is best-effort because native push availability differs by build and device.
  }

  window.localStorage.removeItem(tokenStorageKey(platform))
}
